const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/liho/eCheckin/initialValues.ts';
import { usePatientData } from 'App/store/patient';
import { useSelector } from 'react-redux';
import { useArbeitgeberData } from 'App/store/arbeitgeber';
import { selectUserDetailsFromRegisterWorkItem } from 'App/store/login';
import { normalizePhoneNumber } from 'App/utils/phoneNumberUtils';
import { selectRelationTypes } from 'App/store/metaKat';
import { useRelatives } from 'App/store/relative';
import { useCaseData } from 'App/store/case';
import { getArztDataFromCase } from 'App/utils/cases';
import { findRelationKey } from 'App/utils/person';
import _ from 'lodash';

export const useECheckinInitialValues = (fallNr?: string) => {
    const [pat, patientIsLoading] = usePatientData();
    const loginDetails = useSelector(selectUserDetailsFromRegisterWorkItem);
    const [arbeitgeberInfos] = useArbeitgeberData();
    const [caseData, caseDataIsLoading] = fallNr ? useCaseData(fallNr) : [undefined, false];
    const hausArztData = getArztDataFromCase(caseData, 'HausAz');

    const relationTypes = useSelector(selectRelationTypes);

    const { data: referencePersons, isPendingOrLoading: isRelativesLoading } = fallNr
        ? useRelatives(fallNr)
        : { data: undefined, isPendingOrLoading: false };

    const isLoading = patientIsLoading || isRelativesLoading || caseDataIsLoading;

    const persAngaben = {
        firstname: pat.get('VORNAME') ?? loginDetails?.get('firstname') ?? '',
        lastname: pat.get('NAME') ?? loginDetails?.get('lastname') ?? '',
        fid: fallNr,
        maidenname: pat.get('GEBURTSNAME') ?? '',
        streetAndNumber: pat.get('STRASSE') ?? '',
        plz: pat.get('PLZ') ?? '',
        ort: pat.get('ORT') ?? '',
        country: pat.get('LAND') ?? '',
        gender: pat.get('SEX') ?? '',
        dateOfBirth: (pat.get('GEBDAT') || '').slice(0, 10), // YYYY-MM-DD
        tel: normalizePhoneNumber(pat.get('TELEFON') || pat.get('TELEFON_G')) || '',
        mobile: normalizePhoneNumber(pat.get('TELEFON_M') ?? loginDetails?.get('mobile')) ?? '',
        office: normalizePhoneNumber(pat.get('TELEFON_G')) ?? '',
        email: pat.get('EMAIL') ?? loginDetails?.get('email') ?? '',
        nationality: pat.get('NATIONALITAET') ?? '',
        // zivilstand can contain any freetextvalue which might never match any ot the metakat values
        // zivilstand will only be mapped if the text is equal to one of the dropdown values
        zivilstand: pat.get('ZIVILSTAND') ?? '',
        lang: pat.get('SPRACHE') ?? '',
        ahv: pat.get('SOZVERSNR') ?? '',
        // zivilstand will only be mapped if the text is equal to one of the dropdown values
        religion: pat.get('KONFESSION')
            ? pat.get('KONFESSION').charAt(0).toUpperCase() + pat.get('KONFESSION').slice(1)
            : '',
        profession: pat.get('BERUF') ?? '',
    };

    let angehoerige = {};

    const relatives = _.compact(referencePersons);

    if (relatives && relatives.length > 0 && !isRelativesLoading) {
        if (relatives.length >= 2) {
            angehoerige = {
                beziehung: findRelationKey(relationTypes, relatives[0].VERWANDSCHAFTSGRAD),
                anrede: relatives[0].ANREDE,
                firstname: relatives[0].VORNAME,
                lastname: relatives[0].NAME,
                tel: normalizePhoneNumber(relatives[0].TEL),
                adressZusatz: relatives[0].ZUSATZ,
                streetAndNumber: relatives[0].STRASSE,
                ort: relatives[0].ORT,
                plz: relatives[0].PLZ,
                country: relatives[0].LAND,

                secondbeziehung: findRelationKey(relationTypes, relatives[1].VERWANDSCHAFTSGRAD),
                secondanrede: relatives[1].ANREDE,
                secondfirstname: relatives[1].VORNAME,
                secondlastname: relatives[1].NAME,
                secondtel: normalizePhoneNumber(relatives[1].TEL),
                secondadressZusatz: relatives[1].ZUSATZ,
                secondstreetAndNumber: relatives[1].STRASSE,
                secondort: relatives[1].ORT,
                secondplz: relatives[1].PLZ,
                secondcountry: relatives[1].LAND,
            };
        } else {
            angehoerige = {
                beziehung: findRelationKey(relationTypes, relatives[0].VERWANDSCHAFTSGRAD),
                anrede: relatives[0].ANREDE,
                firstname: relatives[0].VORNAME,
                lastname: relatives[0].NAME,
                tel: normalizePhoneNumber(relatives[0].TEL),
                adressZusatz: relatives[0].ZUSATZ,
                streetAndNumber: relatives[0].STRASSE,
                ort: relatives[0].ORT,
                plz: relatives[0].PLZ,
                country: relatives[0].LAND,
            };
        }
    }

    const arbeitgeber = arbeitgeberInfos?.size
        ? {
              employment: 'employed',
              company: arbeitgeberInfos?.get('Name') ?? '',
              streetAndNumber: arbeitgeberInfos?.get('Strasse') ?? '',
              plz: arbeitgeberInfos?.get('PLZ') ?? '',
              ort: arbeitgeberInfos?.get('Ort') ?? '',
              country: arbeitgeberInfos?.get('Land') ?? '',
              tel: normalizePhoneNumber(arbeitgeberInfos?.get('Tel')) ?? '',
          }
        : {};

    const hausArzt = hausArztData
        ? {
              firstname: hausArztData.Vorname,
              lastname: hausArztData.Name,
              adresszusatz: hausArztData.Zusatz,
              ort: hausArztData.Ort,
              country: hausArztData.Land,
              streetAndNumber: hausArztData.Strasse,
              plz: hausArztData.PLZ,
          }
        : {};

    return [{ persAngaben, arbeitgeber, hausArzt, angehoerige }, isLoading];
};
